import PhysicalAnalysisStore from './PhysicalAnalysisStore';
import {
  STEP_BASIS,
  STEP_COMPLETED,
  STEP_DRAFT,
  STEP_DEFECTS,
  STEP_SIEVES,
} from './physicalAnalysisSteps';

// Lo que llega del servidor: las columnas decimal viajan como texto ("0.0") y `density`,
// que es integer, viaja como el numero 0.
const values = (overrides: any = {}) =>
  ({
    green: '250.0',
    sieve_0: '1.0',
    sieve_12: '2.0',
    sieve_14: '7.0',
    sieve_15: '240.0',
    group_1: '0.0',
    group_2: '1.0',
    broca: '0.0',
    paloteo: '0.0',
    ripio: '0.0',
    guayabas: '0.0',
    caracol: '0.0',
    density: 0,
    observations: null,
    reject_reason: null,
    accepting_reason_id: null,
    step: STEP_SIEVES,
    ...overrides,
  } as any);

const build = (overrides: any = {}) =>
  new PhysicalAnalysisStore(values(overrides), 70, 0.33);

describe('PhysicalAnalysisStore', () => {
  // PLA-1077: un 0 no es "sin valor". Con `values.x || ''` el cero se volvia vacio y el
  // campo se reenviaba en blanco, asi que el formulario pedia el dato y a la vez decia
  // que no se podia modificar porque el paso ya estaba confirmado.
  describe('valores en cero', () => {
    it('conserva el cero de density, que llega como numero', () => {
      expect(build().density).toEqual('0');
    });

    it('conserva el cero de los decimales, que llegan como texto', () => {
      expect(build().group1).toEqual('0.0');
    });

    it('deja vacios los campos que realmente vienen nulos', () => {
      expect(build({ density: null }).density).toEqual('');
      expect(build().observations).toEqual('');
    });
  });

  describe('pasos del asistente', () => {
    it('no es asistente cuando step es null', () => {
      const store = build({ step: null });

      expect(store.isWizard).toBe(false);
      expect(store.showsCalculatedValues).toBe(true);
    });

    it('el paso actual es el siguiente al ultimo confirmado', () => {
      expect(build({ step: STEP_BASIS }).currentStep).toEqual(2);
      expect(build({ step: STEP_COMPLETED }).currentStep).toEqual(STEP_COMPLETED);
    });

    // Con el paso 2 confirmado el analista esta cargando las mallas (paso 3) y todavia
    // no debe ver el factor; con el paso 3 confirmado ya esta en el cierre y si.
    it('no muestra los calculados mientras se cargan las mallas', () => {
      expect(build({ step: STEP_DEFECTS }).showsCalculatedValues).toBe(false);
      expect(build({ step: STEP_DEFECTS }).factor14).toEqual('');
    });

    it('muestra los calculados al llegar al cierre', () => {
      expect(build({ step: STEP_SIEVES }).showsCalculatedValues).toBe(true);
      expect(build({ step: STEP_SIEVES }).factor14).not.toEqual('');
    });

    it('un analisis cerrado no vuelve a pedir confirmacion', () => {
      expect(build({ step: STEP_COMPLETED }).isClosed).toBe(true);
      expect(build({ step: STEP_SIEVES }).isClosed).toBe(false);
    });
  });

  // Dentro del asistente la banda sale de la tabla de parametros y la evalua el
  // componente; el store solo conserva el camino legacy (muestras sueltas).
  describe('suma contra la almendra (camino legacy)', () => {
    it('rechaza una suma que se pasa de la almendra', () => {
      // 1 + 2 + 7 + 240 + 1 (grupo 2) = 251, contra una almendra de 250.
      expect(build({ step: null }).sumMatchesGreen).toBe(false);
    });

    it('acepta la suma exacta', () => {
      expect(build({ step: null, sieve_15: '239.0' }).sumMatchesGreen).toBe(true);
    });

    it('acepta quedarse corto dentro de la tolerancia', () => {
      // 249.5 sobre 250 = 99.8%, dentro del 0.33% de tolerancia.
      expect(build({ step: null, sieve_15: '238.5' }).sumMatchesGreen).toBe(true);
    });
  });

  // PLA-1077 v1.1: ripio y caracol son la misma medicion que la malla 0 y la malla 12. El
  // asistente no los pide y el servidor los guarda en 0.
  describe('ripio y caracol ocultos en el asistente', () => {
    it('los pone en 0 mientras el paso 2 no esta confirmado, como el servidor', () => {
      const store = build({ step: STEP_BASIS, ripio: '0.5', caracol: '0.4' });

      expect(store.ripio).toEqual('0');
      expect(store.caracol).toEqual('0');
    });

    it('no los suma contra la almendra en un borrador nuevo', () => {
      const store = build({ step: STEP_DRAFT, ripio: '3.0', caracol: '3.0' });

      // 1 + 2 + 7 + 240 + 1 = 251
      expect(parseFloat(store.sieveSum)).toEqual(251);
    });

    it('los oculta cuando valen 0', () => {
      const store = build({ step: STEP_DEFECTS });

      expect(store.isHiddenField('ripio')).toBe(true);
      expect(store.isHiddenField('caracol')).toBe(true);
    });

    it('los muestra si un borrador ya confirmo el paso 2 con valores', () => {
      const store = build({ step: STEP_DEFECTS, ripio: '0.5' });

      expect(store.ripio).toEqual('0.5');
      expect(store.isHiddenField('ripio')).toBe(false);
    });

    it('fuera del asistente no oculta ni toca nada', () => {
      const store = build({ step: null, ripio: '0.0', caracol: '0.4' });

      expect(store.isHiddenField('ripio')).toBe(false);
      expect(store.caracol).toEqual('0.4');
    });
  });
});
